import React, { useState, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Briefcase, MapPin, Users, ChevronDown, ChevronUp, CheckCircle, Upload, X, FileText, AlertCircle, Clock, Shield } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB

const Careers = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    position: '',
    resume_url: '',
    location: '',
    availability: '',
    message: '',
    consent: false
  });
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedPosition, setExpandedPosition] = useState(null);
  const fileInputRef = useRef(null);

  const positions = [
    {
      id: 1,
      title: 'Senior AI Ethics & Governance Lead',
      location: 'United Kingdom (Hybrid / Flexible)',
      engagement: 'Permanent or Associate (Full-time / Part-time)',
      overview: 'The Senior AI Ethics & Governance Lead provides strategic leadership on the development and implementation of AI ethics, governance, and policy frameworks. This role works closely with governments, regulators, international organisations, and industry leaders to translate global AI principles into practical, operational governance models.',
      suitedFor: 'The role is suited to professionals with deep experience in public policy, regulation, standards, or responsible AI leadership, and who are comfortable operating across national and international contexts.',
      responsibilities: [
        'Lead the design of national AI strategies and public-sector AI governance frameworks',
        'Advise on ethical, legal, and governance implications of high-impact AI systems',
        'Align organisational and national frameworks with international AI principles and standards',
        'Contribute to standards development, policy consultations, and regulatory engagement',
        'Support leadership and capacity-building initiatives for senior decision-makers'
      ],
      expertise: [
        'Strong experience in AI ethics, governance, public policy, or regulation',
        'Familiarity with international AI frameworks and standards (OECD, UNESCO, EU, IEEE, ISO, NIST)',
        'Ability to engage with senior government officials, regulators, and executives',
        'Excellent policy analysis, writing, and stakeholder engagement skills'
      ]
    },
    {
      id: 2,
      title: 'AI Risk, Assurance & Safety Specialist',
      location: 'United Kingdom (Hybrid / Flexible)',
      engagement: 'Permanent or Associate (Full-time / Part-time)',
      overview: 'The AI Risk, Assurance & Safety Specialist focuses on AI risk management, assurance, auditability, and safety controls across the AI lifecycle. This role supports organisations and governments in operationalising risk-based AI governance, particularly for high-risk, safety-critical, and regulated AI systems.',
      suitedFor: 'This position suits professionals with backgrounds in risk management, assurance, cybersecurity, audit, or safety engineering, combined with AI or advanced technology experience.',
      responsibilities: [
        'Identify, assess, and mitigate AI-related risks across development and deployment',
        'Design AI assurance, audit, and accountability frameworks',
        'Support regulatory readiness for emerging AI and data governance requirements',
        'Integrate AI governance with enterprise risk management, cybersecurity, and data governance',
        'Advise on AI safety guardrails, controls, and monitoring mechanisms'
      ],
      expertise: [
        'Experience in risk management, assurance, audit, cybersecurity, or safety governance',
        'Knowledge of AI lifecycle risks and control mechanisms',
        'Familiarity with standards and frameworks such as ISO/IEC 27001, NIST, and risk-based AI models',
        'Ability to translate technical risk into governance and policy language'
      ]
    },
    {
      id: 3,
      title: 'Sustainable AI & Emerging Technologies Advisor',
      location: 'United Kingdom (Hybrid / Flexible)',
      engagement: 'Permanent or Associate (Full-time / Part-time)',
      overview: 'The Sustainable AI & Emerging Technologies Advisor works at the intersection of AI innovation, sustainability, and societal impact. This role supports the responsible governance of AI and emerging technologies while ensuring alignment with long-term environmental, social, and development goals.',
      suitedFor: 'The role is ideal for professionals with experience in sustainability, innovation policy, emerging technologies, or applied research, particularly in public-interest or global contexts.',
      responsibilities: [
        'Advise on responsible AI use aligned with sustainability and development objectives',
        'Support governance of emerging technologies, including generative AI, robotics, and remote sensing',
        'Align AI initiatives with sustainability, ESG, and long-term value frameworks',
        'Contribute to research, policy development, and cross-sector collaboration',
        'Engage with governments, development agencies, and research institutions'
      ],
      expertise: [
        'Experience in AI, emerging technologies, sustainability, or innovation policy',
        'Understanding of social, environmental, and ethical implications of technology',
        'Familiarity with international development or sustainability frameworks',
        'Strong analytical, research, and stakeholder engagement skills'
      ]
    }
  ];

  const benefits = [
    'High-impact work with governments and international institutions',
    'Opportunities to contribute to global AI policy and standards initiatives',
    'Flexible and inclusive working arrangements',
    'A collaborative, mission-driven environment',
    'The opportunity to shape the future of AI governance and sustainability'
  ];

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const totalSize = selectedFiles.reduce((acc, file) => acc + file.size, 0) + 
                      files.reduce((acc, file) => acc + file.size, 0);
    
    if (totalSize > MAX_FILE_SIZE) {
      toast.error('File size limit exceeded', {
        description: 'Total attachments must not exceed 15 MB.',
      });
      return;
    }
    
    setFiles([...files, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const getTotalFileSize = () => {
    return files.reduce((acc, file) => acc + file.size, 0);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent) {
      toast.error('Consent required', {
        description: 'Please confirm you have read and accept our privacy notice.',
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('position', formData.position);
      submitData.append('resume_url', formData.resume_url);
      submitData.append('message', `Location/Timezone: ${formData.location}\nAvailability: ${formData.availability}\n\n${formData.message}`);
      
      // Append files
      files.forEach((file) => {
        submitData.append('files', file);
      });

      const response = await axios.post(`${API}/careers/apply`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        toast.success('Expression of Interest Submitted!', {
          description: 'Thank you for your interest. We will retain your details and contact you if a suitable opportunity arises.',
        });
        setFormData({ name: '', email: '', position: '', resume_url: '', location: '', availability: '', message: '', consent: false });
        setFiles([]);
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      const errorMessage = error.response?.data?.detail || 'Please try again later or email us directly.';
      toast.error('Failed to submit', {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePosition = (id) => {
    setExpandedPosition(expandedPosition === id ? null : id);
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight mb-8 animate-in fade-in slide-in-from-bottom-8 duration-1000">
              Expression of
              <span className="block font-normal mt-2">Interest</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              We are building a talent pipeline of professionals who share our commitment to safe, responsible, and sustainable AI. Submit an Expression of Interest to be considered for future opportunities as our work and partnerships grow.
            </p>
            
            {/* Important Notice Banner */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-left">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-medium text-amber-900 mb-2">Important Notice</h3>
                  <p className="text-amber-800 text-sm leading-relaxed">
                    The roles listed on this page are <strong>not current vacancies</strong>. We are inviting Expressions of Interest (EOIs) to identify professionals with relevant capabilities for potential future engagement as demand increases. Submitting an EOI does not guarantee an interview or employment.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Talent Pipeline Explanation */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-medium mb-2">Building a Talent Pipeline</h3>
              <p className="text-gray-400 text-sm">We're identifying skilled professionals to support our growing work in Safe and Responsible AI governance.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-medium mb-2">Future Opportunities</h3>
              <p className="text-gray-400 text-sm">We will contact applicants only when a suitable opportunity arises based on demand and role relevance.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-medium mb-2">Your Data is Protected</h3>
              <p className="text-gray-400 text-sm">Your information will be handled in accordance with our privacy policy and used solely for talent pipeline purposes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* EOI Role Areas */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-amber-100 text-amber-800 text-sm font-medium rounded-full mb-6">
              Expression of Interest – Not Active Vacancies
            </div>
            <h2 className="text-4xl sm:text-5xl font-light tracking-tight mb-4">
              Future Role Areas
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We welcome Expressions of Interest from professionals based in the United Kingdom for the following potential future role areas. These listings describe capability profiles we anticipate needing as our work expands.
            </p>
          </div>

          <div className="space-y-6 max-w-4xl mx-auto">
            {positions.map((position, index) => (
              <Card
                key={position.id}
                className="overflow-hidden animate-in fade-in slide-in-from-bottom-4 border-l-4 border-l-amber-400"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-0">
                  {/* Position Header */}
                  <div 
                    className="p-6 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                    onClick={() => togglePosition(position.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-gray-800 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                          <Briefcase size={24} />
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-normal">{position.title}</h3>
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">EOI</span>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center text-sm text-gray-600">
                              <MapPin size={16} className="mr-2 flex-shrink-0" />
                              {position.location}
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                              <Users size={16} className="mr-2 flex-shrink-0" />
                              {position.engagement}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0 ml-4">
                        {expandedPosition === position.id ? (
                          <ChevronUp size={24} className="text-gray-400" />
                        ) : (
                          <ChevronDown size={24} className="text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedPosition === position.id && (
                    <div className="px-6 pb-6 border-t border-gray-100">
                      <div className="pt-6 space-y-6">
                        {/* EOI Notice */}
                        <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                          <p className="text-amber-800 text-sm">
                            <strong>Note:</strong> This is an Expression of Interest for a potential future role, not a current vacancy. We will retain your details and contact you if a relevant opportunity arises.
                          </p>
                        </div>

                        {/* Role Overview */}
                        <div>
                          <h4 className="text-lg font-medium mb-3">Role Overview</h4>
                          <p className="text-gray-700 leading-relaxed mb-3">{position.overview}</p>
                          <p className="text-gray-600 leading-relaxed italic">{position.suitedFor}</p>
                        </div>

                        {/* Key Responsibilities */}
                        <div>
                          <h4 className="text-lg font-medium mb-3">Anticipated Responsibilities</h4>
                          <ul className="space-y-2">
                            {position.responsibilities.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-3">
                                <div className="w-1.5 h-1.5 bg-gray-600 rounded-full mt-2 flex-shrink-0"></div>
                                <span className="text-gray-700">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Expertise & Background */}
                        <div>
                          <h4 className="text-lg font-medium mb-3">Desired Expertise & Background</h4>
                          <ul className="space-y-2">
                            {position.expertise.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-3">
                                <div className="w-1.5 h-1.5 bg-gray-600 rounded-full mt-2 flex-shrink-0"></div>
                                <span className="text-gray-700">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Express Interest Button */}
                        <div className="pt-4">
                          <Button
                            variant="outline"
                            className="hover:scale-105 transition-transform duration-200 border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white"
                            onClick={() => {
                              document.getElementById('eoi-form').scrollIntoView({ behavior: 'smooth' });
                              setFormData({ ...formData, position: position.title });
                            }}
                          >
                            Submit Expression of Interest
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Working with Us */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-8">
              Working with the Centre for Sustainable AI
            </h2>
            <p className="text-lg text-gray-600 mb-8">When opportunities arise, we offer:</p>
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-gray-700">{benefit}</p>
                </div>
              ))}
            </div>
            <p className="text-lg text-gray-600 mt-8 italic">
              We welcome Expressions of Interest from professionals based in the United Kingdom who are passionate about responsible AI, public value, and long-term societal impact.
            </p>
          </div>
        </div>
      </section>

      {/* EOI Form */}
      <section id="eoi-form" className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block px-4 py-2 bg-amber-100 text-amber-800 text-sm font-medium rounded-full mb-6">
                Talent Pipeline Registration
              </div>
              <h2 className="text-4xl sm:text-5xl font-light tracking-tight mb-4">
                Submit Expression of Interest
              </h2>
              <p className="text-lg text-gray-600">
                Complete the form below to register your interest in future opportunities with the Centre for Sustainable AI.
              </p>
            </div>

            {/* Disclaimer Card */}
            <Card className="mb-8 bg-gray-100 border-gray-200">
              <CardContent className="p-6">
                <h3 className="font-medium text-gray-900 mb-3">Before you submit, please note:</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400">•</span>
                    <span>These roles are <strong>not currently advertised vacancies</strong>.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400">•</span>
                    <span>Submitting an EOI <strong>does not guarantee an interview or employment</strong>.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400">•</span>
                    <span>We will contact applicants <strong>only if a suitable opportunity arises</strong> based on demand and role relevance.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400">•</span>
                    <span>Your details will be retained securely and used solely for talent pipeline purposes in accordance with our <a href="/privacy" className="underline hover:text-black">Privacy Policy</a>.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="shadow-lg">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        data-testid="career-name-input"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="career-email-input"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="position">Role Area of Interest *</Label>
                    <select
                      id="position"
                      data-testid="career-position-input"
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      required
                      className="mt-2 w-full h-10 px-3 rounded-md border border-input bg-background text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">Select a role area...</option>
                      {positions.map((pos) => (
                        <option key={pos.id} value={pos.title}>{pos.title}</option>
                      ))}
                      <option value="Other">Other / General Interest</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="location">Location / Timezone *</Label>
                      <Input
                        id="location"
                        data-testid="career-location-input"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        placeholder="e.g., London, UK (GMT)"
                        required
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="availability">Availability</Label>
                      <select
                        id="availability"
                        data-testid="career-availability-input"
                        value={formData.availability}
                        onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                        className="mt-2 w-full h-10 px-3 rounded-md border border-input bg-background text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <option value="">Select availability...</option>
                        <option value="Immediately available">Immediately available</option>
                        <option value="1-3 months notice">1-3 months notice</option>
                        <option value="3-6 months notice">3-6 months notice</option>
                        <option value="6+ months notice">6+ months notice</option>
                        <option value="Open to discussion">Open to discussion</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="resume_url">LinkedIn Profile or Portfolio URL</Label>
                    <Input
                      id="resume_url"
                      type="url"
                      data-testid="career-resume-input"
                      value={formData.resume_url}
                      onChange={(e) => setFormData({ ...formData, resume_url: e.target.value })}
                      placeholder="https://"
                      className="mt-2"
                    />
                  </div>

                  {/* File Upload Section */}
                  <div>
                    <Label>CV & Supporting Documents *</Label>
                    <p className="text-sm text-gray-500 mb-3">Upload your CV and any supporting documents (max 15 MB total)</p>
                    
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.rtf"
                      className="hidden"
                    />
                    
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-gray-400 transition-colors duration-200"
                    >
                      <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm text-gray-600">Click to upload files</p>
                      <p className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX, TXT, RTF accepted</p>
                    </div>

                    {/* File List */}
                    {files.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {files.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-gray-500" />
                              <div>
                                <p className="text-sm font-medium text-gray-700">{file.name}</p>
                                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="p-1 hover:bg-gray-200 rounded transition-colors duration-200"
                            >
                              <X className="w-4 h-4 text-gray-500" />
                            </button>
                          </div>
                        ))}
                        <div className="text-right text-sm text-gray-500">
                          Total: {formatFileSize(getTotalFileSize())} / 15 MB
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="message">Capability Summary / Message</Label>
                    <Textarea
                      id="message"
                      data-testid="career-message-input"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={5}
                      className="mt-2"
                      placeholder="Briefly describe your relevant experience, skills, and why you're interested in working with the Centre for Sustainable AI..."
                    />
                  </div>

                  {/* Consent Checkbox */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.consent}
                        onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                        required
                      />
                      <span className="text-sm text-gray-700">
                        I understand that this is an Expression of Interest, not an application for a current vacancy. I consent to the Centre for Sustainable AI retaining my details for talent pipeline purposes and contacting me if a relevant opportunity arises. I have read and accept the <a href="/privacy" className="underline hover:text-black">Privacy Policy</a>. *
                      </span>
                    </label>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    data-testid="career-submit-btn"
                    className="w-full hover:scale-105 transition-transform duration-200"
                    disabled={isSubmitting || !formData.consent}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Expression of Interest'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Additional Contact Info */}
            <div className="mt-8 text-center">
              <p className="text-gray-600 text-sm">
                For general enquiries about working with the Centre for Sustainable AI, please <a href="/contact" className="underline hover:text-black">contact us</a>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
